// ==============================================
// Register Page (Email/Password)
// ==============================================

import { useRef, useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../providers/AuthProvider';
import { useRevealFocusedField } from '../../lib/viewport/useRevealFocusedField';

export function RegisterPage() {
  const { isAuthenticated, isLoading, register, error, clearError } = useAuth();
  const [searchParams] = useSearchParams();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Keep the focused field visible above the Scout keyboard within this page's
  // bounded scroll region (geometry stays owned by ViewportProvider).
  const scrollRef = useRef<HTMLDivElement>(null);
  // Keep the focused field AND the Create Account button framed together above the
  // Scout keyboard (the button is the companion; geometry stays owned by the hook).
  const createButtonRef = useRef<HTMLButtonElement>(null);
  useRevealFocusedField(scrollRef, { companionRef: createButtonRef });

  const passwordLongEnough = password.length >= 8;
  const passwordsMatch = password === confirmPassword;

  // After registration, redirect to subscription page with package/promo params if present
  if (isAuthenticated && !isLoading) {
    const packageParam = searchParams.get('package');
    if (packageParam) {
      const promoParam = searchParams.get('promo');
      const qs = new URLSearchParams({ package: packageParam });
      if (promoParam) qs.set('promo', promoParam);
      return <Navigate to={`/dashboard/checkout?${qs.toString()}`} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      await register(email, password, name || undefined);
      // Redirect will happen automatically via the isAuthenticated check
    } catch {
      // Error is handled by AuthProvider
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = validationError || error;

  return (
    // Bounded to the normalized usable viewport height so the taller Register form
    // stays reachable when the Scout keyboard is open: ViewportProvider publishes
    // --vortex-usable-height (100vh fallback in ordinary browsers, preserving the
    // desktop/iPhone appearance). This element owns vertical scrolling; the inner
    // group centres with auto margins that yield to a reachable top position when
    // the content is taller than the usable region, so Confirm Password, Create
    // Account, and the footer can be scrolled to.
    // See docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md.
    <div
      ref={scrollRef}
      className="flex overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4"
      style={{ height: 'var(--vortex-usable-height, 100vh)' }}
    >
      <div className="w-full max-w-md m-auto">
        {/* Logo */}
        <div className="text-center mb-2">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src="/vortex-logo-white.svg" alt="Vortex Marine" className="w-10 h-10" />
            <span className="text-2xl font-bold text-white">Vortex Marine</span>
          </div>
        </div>

        {/* Register Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 shadow-xl">
          <h1 className="text-2xl font-bold text-white text-center mb-4">Create Account</h1>

          {displayError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm whitespace-pre-line">
              {displayError}
              {displayError.toLowerCase().includes('already exists') && (
                <div className="mt-3 pt-3 border-t border-red-500/30">
                  <Link
                    to="/forgot-password"
                    className="text-cyan-400 hover:underline font-medium inline-flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Forgot your password?
                  </Link>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Input */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                Full Name <span className="text-slate-500">(optional)</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                placeholder="John Smith"
              />
            </div>

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
              />
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-10 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              {password && !passwordLongEnough ? (
                <p className="mt-1 text-xs text-amber-400">Password must be at least 8 characters</p>
              ) : (
                <p className="mt-1 text-xs text-slate-500">
                  Must be at least 8 characters with uppercase, lowercase, and a number
                </p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-10 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                >
                  {showConfirm ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="mt-1 text-xs text-red-400">Passwords do not match</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              ref={createButtonRef}
              type="submit"
              disabled={isSubmitting || isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50 mt-6"
            >
              {isSubmitting || isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating account...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Create Account
                </>
              )}
            </button>
          </form>

          {/* Benefits */}
          <div className="mt-6 space-y-2">
            <p className="text-xs text-slate-400 text-center font-medium mb-3">What you get:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Custom marine charts
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                MBTiles downloads
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Order history
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Usage tracking
              </div>
            </div>
          </div>

          {/* Terms */}
          <p className="mt-6 text-xs text-center text-slate-500">
            By creating an account, you agree to our{' '}
            <a href="/terms" className="text-cyan-400 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</a>
          </p>
        </div>

        {/* Footer Links */}
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:underline font-medium">
              Sign in
            </Link>
          </p>
          <a href="/" className="text-sm text-slate-500 hover:text-white transition-colors block">
            Back to home
          </a>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
